import { safeLocalStorage } from '@toss/storage';

import {
  DEFAULT_FROM,
  DEFAULT_LOCATION,
  REFERRER_STORAGE_KEY,
  REFERRER_TTL_MS,
} from '../constants';
import type { ReferrerData, ReferrerResult, TrackingLocation } from '../types';

/**
 * Referrer 데이터를 localStorage에 저장
 *
 * @param from - 이전 페이지 경로 (예: router.asPath)
 * @param location - 진입 버튼/링크 식별자
 *
 * @example
 * ```tsx
 * // Navigation point에서 사용
 * <Link
 *   href="/build"
 *   onClick={() => setReferrer('/', 'home_cta')}
 * >
 *   퀴즈 만들기
 * </Link>
 * ```
 */
export function setReferrer(properties: {
  from: string;
  location?: string;
}): void {
  const { from, location } = properties;
  try {
    const data: ReferrerData = {
      from,
      location,
      timestamp: Date.now(),
    };

    safeLocalStorage.set(REFERRER_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    // localStorage 접근 실패 시 무시 (SSR, private browsing 등)
    console.warn('[enter-referrer-tracking] Failed to set referrer:', error);
  }
}

/**
 * Referrer 데이터를 localStorage에서 읽어옴
 * TTL 검증 후, 유효한 데이터만 반환하고 즉시 삭제 (일회성)
 *
 * @returns ReferrerResult - { from, location }
 *
 * @example
 * ```tsx
 * // Page feature에서 사용
 * const referrer = getReferrer();
 * // → { from: '/', location: 'home_cta' }
 * ```
 */
export function getReferrer(): ReferrerResult | null {
  try {
    const stored = safeLocalStorage.get(REFERRER_STORAGE_KEY);

    if (!stored) {
      return { from: DEFAULT_FROM, location: DEFAULT_LOCATION };
    }

    const data: ReferrerData = JSON.parse(stored);

    // TTL 검증: 5분 이내 데이터만 유효
    const isExpired = Date.now() - data.timestamp > REFERRER_TTL_MS;

    if (isExpired) {
      clearReferrer();
      return { from: DEFAULT_FROM, location: DEFAULT_LOCATION };
    }

    return {
      from: data.from,
      location: data.location as TrackingLocation | undefined,
    };
  } catch (error) {
    // 파싱 실패, localStorage 접근 실패 시 기본값 반환
    console.warn('[enter-referrer-tracking] Failed to get referrer:', error);
    return { from: DEFAULT_FROM, location: DEFAULT_LOCATION };
  }
}

/**
 * Referrer 데이터를 localStorage에서 삭제
 *
 * @internal - 주로 getReferrer 내부에서 사용됨
 */
export function clearReferrer(): void {
  try {
    safeLocalStorage.remove(REFERRER_STORAGE_KEY);
  } catch (error) {
    console.warn('[enter-referrer-tracking] Failed to clear referrer:', error);
  }
}
